import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { EyeOutlined, CloseOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import Modal from 'antd/lib/modal/Modal';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { message, Spin, Button } from 'antd';
import { getPodUrl, updatePodStatus } from 'store/approvalSlice';
import { getSignedUrl } from 'store/commonSlice';
import { getUIVersion } from 'utils/router-static-data';

// Styled Components
const ModalWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: center;
	padding: 0px 24px 24px 24px;
`;

const ModalHeader = styled.div`
	border-bottom: 0.5px solid #c6c6c8;
	height: 48px;
	width: 100%;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: flex-end;
	padding-right: 24px;
`;

const CloseButton = styled.button`
	background: transparent;
	border: none;
	cursor: pointer;
	font-size: 18px;
	color: #858384;
	display: flex;
	align-items: center;
	justify-content: center;
	&:hover {
		color: #000;
	}
`;

const TabsContainer = styled.div`
	display: flex;
	gap: 8px;
	align-items: center;
	padding: 12px 0;
	width: 100%;
`;

const Tab = styled.button`
	background: transparent;
	border: none;
	border-bottom: ${(props) => (props.active ? '2px solid #1990ff' : 'none')};
	padding: 8px;
	cursor: pointer;
	font-size: 16px;
	font-weight: ${(props) => (props.active ? '600' : '400')};
	color: ${(props) => (props.active ? '#1990ff' : '#000')};
	font-family: 'Inter', sans-serif;
	&:hover {
		color: #1990ff;
	}
`;

const ContentArea = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: center;
`;

const InfoRow = styled.div`
	display: flex;
	justify-content: space-between;
	width: 100%;
	margin-bottom: 24px;
`;

const InfoItem = styled.div`
	display: flex;
	gap: 24px;
	align-items: center;
`;

const InfoLabel = styled.span`
	color: #858384;
	font-size: 14px;
	font-family: 'Inter', sans-serif;
`;

const InfoValue = styled.span`
	color: #000;
	font-size: 14px;
	font-weight: 600;
	font-family: 'Inter', sans-serif;
`;

const ChargesCard = styled.div`
	border: 1px solid #c6c6c6;
	border-radius: 5px;
	padding: 8px 19px;
	display: flex;
	flex-direction: column;
	gap: 9px;
	align-items: center;
`;

const ChargesHeader = styled.div`
	display: flex;
	justify-content: flex-end;
	width: 462px;
`;

const ChargesHeaderRow = styled.div`
	display: flex;
	justify-content: space-between;
	width: 298px;
`;

const ChargesHeaderCell = styled.div`
	width: 135px;
	text-align: center;
	font-weight: 600;
	font-size: 14px;
	color: #000;
	font-family: 'Inter', sans-serif;
`;

const ChargesBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	width: 100%;
`;

const ChargesRow = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	width: 100%;
`;

const ChargesLabel = styled.span`
	color: #858384;
	font-size: 14px;
	font-family: 'Inter', sans-serif;
`;

const ChargesValuesContainer = styled.div`
	display: flex;
	gap: 24px;
`;

const ChargesInputBox = styled.div`
	width: 137px;
	height: 40px;
	background: #ececec;
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	display: flex;
	align-items: center;
	padding: 0 12px;
	gap: 10px;
`;

const RupeeIcon = styled.span`
	color: rgba(0, 0, 0, 0.3);
	font-size: 15px;
`;

const ChargesValue = styled.span`
	color: rgba(0, 0, 0, 0.6);
	font-size: 12px;
	font-family: 'Inter', sans-serif;
`;

const ImageContainer = styled.div`
	width: 844px;
	height: 444px;
	background: #d9d9d9;
	border-radius: 2px;
	overflow: hidden;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const PreviewLayout = styled.div`
	display: grid;
	grid-template-columns: 260px 1fr;
	min-height: 520px;
	width: 100%;
`;

const Sidebar = styled.div`
	border-right: 1px solid #f0f0f0;
	padding: 12px;
	overflow: auto;
`;

const PreviewPane = styled.div`
	padding: 16px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	background: #fff;
	max-width: 1038px;
`;

const FileRow = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	padding: 10px 12px;
	border-radius: 8px;
	cursor: pointer;
	background: ${(props) => (props.active ? '#f0f6ff' : 'transparent')};

	&:hover {
		background: ${(props) => (props.active ? '#f0f6ff' : '#fafafa')};
	}
`;

const FileName = styled.div`
	flex: 1;
	min-width: 0;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	color: #1f1f1f;
	font-size: 13px;
`;

const DotContainer = styled.div`
	display: flex;
	justify-content: center;
	gap: 8px;
	margin-top: 16px;
`;

const Dot = styled.div`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${(props) => (props.active ? '#1890ff' : '#d9d9d9')};
	cursor: pointer;
	transition: background-color 0.3s;

	&:hover {
		background-color: ${(props) => (props.active ? '#1890ff' : '#bfbfbf')};
	}
`;

const ArrowButton = styled(Button)`
	border: none;
	background: transparent;
	box-shadow: none;
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	padding: 0 16px;

	.anticon {
		font-size: 24px;
		color: #8c8c8c;
	}

	&:hover .anticon {
		color: #1f1f1f;
	}

	&:disabled .anticon {
		color: #d9d9d9;
	}
`;

const FooterButtons = styled.div`
	display: flex;
	gap: 8px;
	justify-content: flex-end;
	width: 100%;
	margin-top: 12px;
`;

const ActionButton = styled.button`
	width: 135.5px;
	height: 40px;
	border: 0.5px solid ${(props) => props.borderColor || '#000'};
	border-radius: 2px;
	background: transparent;
	color: ${(props) => props.borderColor || '#000'};
	font-size: 16px;
	font-weight: 500;
	font-family: 'Inter', sans-serif;
	cursor: pointer;
	&:hover {
		opacity: 0.8;
	}
	&:disabled {
		opacity: 0.45;
		cursor: not-allowed;
	}
`;

const NoDataMessage = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 200px;
	color: #858384;
	font-size: 16px;
	font-family: 'Inter', sans-serif;
`;

// Rejection Reason Modal Styled Components
const RejectModalContent = styled.div`
	display: flex;
	flex-direction: column;
	gap: 48px;
	padding: 26px 32px 28px 32px;
`;

const RejectModalTitle = styled.h2`
	font-family: 'Inter', sans-serif;
	font-weight: 600;
	font-size: 24px;
	color: #000;
	margin: 0;
`;

const RejectFormGroup = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const RejectLabel = styled.label`
	font-family: 'Inter', sans-serif;
	font-weight: 400;
	font-size: 16px;
	color: #0c0c0c;
`;

const RejectTextArea = styled.textarea`
	border: 1px solid #c6c6c8;
	border-radius: 2px;
	padding: 10px 12px;
	height: 68px;
	font-family: 'Inter', sans-serif;
	font-size: 12px;
	resize: none;
	&::placeholder {
		color: #a1a1a1;
	}
	&:focus {
		outline: none;
		border-color: #143e60;
	}
`;

const RejectModalButtons = styled.div`
	display: flex;
	gap: 27px;
	justify-content: center;
	padding: 10px;
`;

const RejectCancelButton = styled.button`
	flex: 1;
	max-width: 135.5px;
	height: 40px;
	border: 1px solid #ff3b30;
	border-radius: 4px;
	background: transparent;
	color: #ff3b30;
	font-family: 'Inter', sans-serif;
	font-weight: 500;
	font-size: 16px;
	cursor: pointer;
	&:hover {
		opacity: 0.8;
	}
`;

const RejectSubmitButton = styled.button`
	flex: 1;
	max-width: 135.5px;
	height: 40px;
	border: none;
	border-radius: 4px;
	background: #143e60;
	color: #fff;
	font-family: 'Inter', sans-serif;
	font-weight: 500;
	font-size: 16px;
	cursor: pointer;
	&:hover {
		opacity: 0.8;
	}
	&:disabled {
		opacity: 0.5;
		cursor: not-allowed;
	}
`;

const processPodUrls = (urlData) => {
	if (!urlData) return [];
	if (Array.isArray(urlData)) return urlData;
	if (typeof urlData === 'string') {
		return urlData
			.split(',')
			.map((u) => u.trim())
			.filter(Boolean);
	}
	return [];
};

const normalizeToArray = (value) => {
	if (Array.isArray(value)) return value;
	if (typeof value === 'string' && value.includes(',')) {
		return value
			.split(',')
			.map((v) => v.trim())
			.filter(Boolean);
	}
	if (value) return [value];
	return [];
};

export function PodViewerModal({ id, data }) {
	const isV2UI = getUIVersion() === 'new';
	const porBlocked =
		isV2UI && !!data?.por_approval_status && data.por_approval_status !== 'approved';
	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('pod');
	const [url, setUrl] = useState('');
	const [extension, setExtension] = useState('');
	const [podUrls, setPodUrls] = useState([]);
	const [activePodUrl, setActivePodUrl] = useState('');
	const [proofUrl, setProofUrl] = useState('');
	const [proofExtension, setProofExtension] = useState('');
	const [proofUrls, setProofUrls] = useState([]);
	const [activeProofUrl, setActiveProofUrl] = useState('');
	const [loading, setLoading] = useState(false);
	const [showRejectModal, setShowRejectModal] = useState(false);
	const [rejectionReason, setRejectionReason] = useState('');
	const [rejectLoading, setRejectLoading] = useState(false);
	const dispatch = useDispatch();

	const handleCancel = () => {
		setOpen(false);
		setUrl('');
		setExtension('');
		setPodUrls([]);
		setActivePodUrl('');
		setProofUrl('');
		setProofExtension('');
		setProofUrls([]);
		setActiveProofUrl('');
		setActiveTab('pod');
		setShowRejectModal(false);
		setRejectionReason('');
	};
	// eslint-disable-next-line sonarjs/cognitive-complexity
	useEffect(() => {
		if (!open || !id) return;

		setLoading(true);

		dispatch(getPodUrl({ id }))
			.then(async (res) => {
				const podUrlData = res.rows.proof_of_delivery_url;
				if (isV2UI) {
					const driverPodKeys = normalizeToArray(data?.driver_pod_keys);
					let driverPodUrls = [];
					if (driverPodKeys.length) {
						const signedUrlResponse = await dispatch(
							getSignedUrl({
								url_type: 'view',
								data: {
									entity: 'proof_of_delivery_upload',
									keys: driverPodKeys,
								},
							})
						);
						driverPodUrls = (signedUrlResponse?.data || [])
							.map((item) => item?.url)
							.filter(Boolean);
					}

					const urls = [...processPodUrls(podUrlData), ...driverPodUrls];
					setPodUrls(urls);
					if (urls.length > 0) setActivePodUrl(urls[0]);
				} else if (podUrlData) {
					const e = podUrlData.split('.').pop();
					const ext = e.split('?')[0];
					setExtension(ext);
					setUrl(podUrlData);
				}

				if (res.rows.additional_charges_proof_url) {
					const additionalProofData = res.rows.additional_charges_proof_url;
					if (isV2UI) {
						const urls = processPodUrls(additionalProofData);
						setProofUrls(urls);
						if (urls.length > 0) setActiveProofUrl(urls[0]);
					} else {
						let singleUrl = additionalProofData;
						if (Array.isArray(singleUrl)) {
							// eslint-disable-next-line prefer-destructuring
							singleUrl = singleUrl[0];
						}
						// Support comma separated strings gracefully for older structures
						if (typeof singleUrl === 'string' && singleUrl.includes(',')) {
							singleUrl = singleUrl.split(',')[0].trim();
						}
						if (singleUrl) {
							const proofE = singleUrl.split('.').pop();
							const proofExt = proofE.split('?')[0];
							setProofExtension(proofExt);
							setProofUrl(singleUrl);
						}
					}
				}
			})
			.finally(() => setLoading(false));
	}, [open, id, dispatch, isV2UI, data?.driver_pod_keys]);

	// Fetch proof URL when user navigates to proof tab
	const handleTabChange = (tab) => {
		setActiveTab(tab);
	};

	const getExt = (fileUrl) => {
		if (!fileUrl) return '';
		return fileUrl.split('.').pop().split('?')[0].toLowerCase();
	};

	const handlePrevPod = () => {
		const idx = podUrls.indexOf(activePodUrl);
		if (idx > 0) setActivePodUrl(podUrls[idx - 1]);
	};

	const handleNextPod = () => {
		const idx = podUrls.indexOf(activePodUrl);
		if (idx < podUrls.length - 1) setActivePodUrl(podUrls[idx + 1]);
	};

	const handleReject = () => {
		setShowRejectModal(true);
	};

	const handleRejectCancel = () => {
		setShowRejectModal(false);
		setRejectionReason('');
	};

	const handleRejectSubmit = () => {
		if (!rejectionReason.trim()) {
			message.warning('Please enter a reason for rejection');
			return;
		}
		setRejectLoading(true);
		dispatch(
			updatePodStatus({
				id,
				status: 'Rejected',
				rejection_reason: rejectionReason.trim(),
				trip_id: data?.trip_id,
			})
		)
			.then(() => {
				message.error('Rejected');
				setOpen(false);
				setShowRejectModal(false);
				setRejectionReason('');
				window.location.reload();
			})
			.catch(() => message.error('Error while rejecting'))
			.finally(() => setRejectLoading(false));
	};

	const handleApprove = () => {
		dispatch(updatePodStatus({ id, status: 'Accepted' }))
			.then(() => {
				message.success('Approved');
				setOpen(false);
				window.location.reload();
			})
			.catch(() => message.error('Error while approving'));
	};

	const renderSinglePodTab = () => {
		if (extension === 'pdf') {
			return (
				<ImageContainer>
					<iframe
						title="pod-pdf"
						src={url}
						width="100%"
						height="100%"
						style={{ border: 'none' }}
					/>
				</ImageContainer>
			);
		}

		return (
			<ImageContainer>
				<iframe
					title="pod-img"
					srcDoc={`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100%;background:#d9d9d9;"><img src="${url}" style="max-width: 100%; max-height: 100%; object-fit: contain;" /></body></html>`}
					width="100%"
					height="100%"
					style={{ border: 'none' }}
				/>
			</ImageContainer>
		);
	};

	const renderPodTab = () => {
		if (loading) {
			return (
				<ImageContainer>
					<Spin size="large" />
				</ImageContainer>
			);
		}

		if (!isV2UI && !url) {
			return (
				<ImageContainer>
					<NoDataMessage>No POD available</NoDataMessage>
				</ImageContainer>
			);
		}

		if (isV2UI && podUrls.length === 0) {
			return (
				<ImageContainer>
					<NoDataMessage>No POD available</NoDataMessage>
				</ImageContainer>
			);
		}

		if (!isV2UI) {
			return renderSinglePodTab();
		}

		const activeExt = getExt(activePodUrl);

		const renderActiveFile = () => {
			if (activeExt === 'pdf') {
				return (
					<iframe
						title="pod-pdf"
						src={activePodUrl}
						width="100%"
						height="100%"
						style={{ border: 'none', minHeight: '444px' }}
					/>
				);
			}
			return (
				<iframe
					title="pod-img"
					srcDoc={`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100%;background:#d9d9d9;"><img src="${activePodUrl}" style="max-width: 100%; max-height: 100%; object-fit: contain;" /></body></html>`}
					width="100%"
					height="100%"
					style={{ border: 'none', minHeight: '444px' }}
				/>
			);
		};

		if (podUrls.length === 1) {
			return <ImageContainer>{renderActiveFile()}</ImageContainer>;
		}

		return (
			<PreviewLayout>
				<Sidebar>
					<div
						style={{
							fontWeight: 600,
							fontSize: 12,
							color: '#6b7280',
							marginBottom: 16,
						}}
					>
						UPLOAD POD - PREVIEW
					</div>
					{podUrls.map((pUrl, idx) => {
						const fName = pUrl.split('/').pop().split('?')[0] || `Pod_${idx + 1}`;
						return (
							<FileRow
								key={pUrl}
								active={pUrl === activePodUrl}
								onClick={() => setActivePodUrl(pUrl)}
							>
								<FileName title={fName}>{fName}</FileName>
								{pUrl === activePodUrl && (
									<div style={{ marginLeft: 'auto', paddingLeft: 8 }}>
										<svg
											width="14"
											height="14"
											viewBox="0 0 14 14"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M5.25 10.5L8.75 7L5.25 3.5"
												stroke="#1f1f1f"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
							</FileRow>
						);
					})}
				</Sidebar>
				<PreviewPane>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							width: '100%',
							flex: 1,
							minHeight: 0,
						}}
					>
						<ArrowButton
							icon={<LeftOutlined />}
							onClick={handlePrevPod}
							disabled={podUrls.indexOf(activePodUrl) <= 0}
						/>
						<div
							style={{
								flex: 1,
								height: '100%',
								minHeight: '444px',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								overflow: 'hidden',
								background: '#d9d9d9',
								borderRadius: '2px',
							}}
						>
							{renderActiveFile()}
						</div>
						<ArrowButton
							icon={<RightOutlined />}
							onClick={handleNextPod}
							disabled={podUrls.indexOf(activePodUrl) === podUrls.length - 1}
						/>
					</div>
					<DotContainer>
						{podUrls.map((pUrl) => (
							<Dot
								key={pUrl}
								active={pUrl === activePodUrl}
								onClick={() => setActivePodUrl(pUrl)}
							/>
						))}
					</DotContainer>
				</PreviewPane>
			</PreviewLayout>
		);
	};

	const renderAdditionalChargesTab = () => {
		const charges = data?.additional_charges;

		return (
			<ContentArea>
				<InfoRow>
					<InfoItem>
						<InfoLabel>Project Location:</InfoLabel>
						<InfoValue>{data?.project_location || '-'}</InfoValue>
					</InfoItem>
					<InfoItem>
						<InfoLabel>City:</InfoLabel>
						<InfoValue>{data?.city || '-'}</InfoValue>
					</InfoItem>
					<InfoItem>
						<InfoLabel>Vendor name:</InfoLabel>
						<InfoValue>{data?.vendor_name || '-'}</InfoValue>
					</InfoItem>
				</InfoRow>

				<ChargesCard>
					<ChargesHeader>
						<ChargesHeaderRow>
							<ChargesHeaderCell>Vendor</ChargesHeaderCell>
							<ChargesHeaderCell>Client</ChargesHeaderCell>
						</ChargesHeaderRow>
					</ChargesHeader>

					<ChargesBody>
						<ChargesRow>
							<ChargesLabel>Halt Charges</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.haltCharges || 0}</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.clientHaltCharges || 0}</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>

						<ChargesRow>
							<ChargesLabel>Miscellaneous Charges</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>
										{charges?.miscellaneousCharges || 0}
									</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>
										{charges?.clientMiscellaneousCharges || 0}
									</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>

						<ChargesRow>
							<ChargesLabel>Toll Charges</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.tollCharges || 0}</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.clientTollCharges || 0}</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>

						<ChargesRow>
							<ChargesLabel>Unloading Charges</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.unloadingCharges || 0}</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>
										{charges?.clientUnloadingCharges || 0}
									</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>

						<ChargesRow>
							<ChargesLabel>Extra KM</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.extraKmCharges || 0}</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>
										{charges?.clientExtraKmCharges || 0}
									</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>

						<ChargesRow>
							<ChargesLabel>Extra HR</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.extraHrCharges || 0}</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>
										{charges?.clientExtraHrCharges || 0}
									</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>

						<ChargesRow>
							<ChargesLabel>Labour Charges</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.labourCharges || 0}</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.clientLabourCharges || 0}</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>

						<ChargesRow>
							<ChargesLabel>Deduction Amount</ChargesLabel>
							<ChargesValuesContainer>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>{charges?.deductionAmount || 0}</ChargesValue>
								</ChargesInputBox>
								<ChargesInputBox>
									<RupeeIcon>₹</RupeeIcon>
									<ChargesValue>
										{charges?.clientDeductionAmount || 0}
									</ChargesValue>
								</ChargesInputBox>
							</ChargesValuesContainer>
						</ChargesRow>
					</ChargesBody>
				</ChargesCard>
			</ContentArea>
		);
	};

	// eslint-disable-next-line sonarjs/cognitive-complexity
	const renderProofTab = () => {
		if (loading) {
			return (
				<ImageContainer>
					<Spin size="large" />
				</ImageContainer>
			);
		}

		if (!isV2UI && !proofUrl) {
			return (
				<ImageContainer>
					<NoDataMessage>No proof of additional charges available</NoDataMessage>
				</ImageContainer>
			);
		}

		if (isV2UI && proofUrls.length === 0) {
			return (
				<ImageContainer>
					<NoDataMessage>No proof of additional charges available</NoDataMessage>
				</ImageContainer>
			);
		}

		if (!isV2UI) {
			if (proofExtension === 'pdf') {
				return (
					<ImageContainer>
						<iframe
							title="proof-pdf"
							src={proofUrl}
							width="100%"
							height="100%"
							style={{ border: 'none' }}
						/>
					</ImageContainer>
				);
			}

			return (
				<ImageContainer>
					<iframe
						title="proof-img"
						srcDoc={`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100%;background:#d9d9d9;"><img src="${proofUrl}" style="max-width: 100%; max-height: 100%; object-fit: contain;" /></body></html>`}
						width="100%"
						height="100%"
						style={{ border: 'none' }}
					/>
				</ImageContainer>
			);
		}

		const activeExt = getExt(activeProofUrl);

		const renderActiveFile = () => {
			if (activeExt === 'pdf') {
				return (
					<iframe
						title="proof-pdf"
						src={activeProofUrl}
						width="100%"
						height="100%"
						style={{ border: 'none', minHeight: '444px' }}
					/>
				);
			}
			return (
				<iframe
					title="proof-img"
					srcDoc={`<html><body style="margin:0;display:flex;align-items:center;justify-content:center;height:100%;background:#d9d9d9;"><img src="${activeProofUrl}" style="max-width: 100%; max-height: 100%; object-fit: contain;" /></body></html>`}
					width="100%"
					height="100%"
					style={{ border: 'none', minHeight: '444px' }}
				/>
			);
		};

		if (proofUrls.length === 1) {
			return <ImageContainer>{renderActiveFile()}</ImageContainer>;
		}

		const handlePrevProof = () => {
			const idx = proofUrls.indexOf(activeProofUrl);
			if (idx > 0) setActiveProofUrl(proofUrls[idx - 1]);
		};

		const handleNextProof = () => {
			const idx = proofUrls.indexOf(activeProofUrl);
			if (idx < proofUrls.length - 1) setActiveProofUrl(proofUrls[idx + 1]);
		};

		return (
			<PreviewLayout>
				<Sidebar>
					<div
						style={{
							fontWeight: 600,
							fontSize: 12,
							color: '#6b7280',
							marginBottom: 16,
						}}
					>
						PROOF OF ADDITIONAL CHARGES
					</div>
					{proofUrls.map((pUrl, idx) => {
						const fName = pUrl.split('/').pop().split('?')[0] || `Proof_${idx + 1}`;
						return (
							<FileRow
								key={pUrl}
								active={pUrl === activeProofUrl}
								onClick={() => setActiveProofUrl(pUrl)}
							>
								<FileName title={fName}>{fName}</FileName>
								{pUrl === activeProofUrl && (
									<div style={{ marginLeft: 'auto', paddingLeft: 8 }}>
										<svg
											width="14"
											height="14"
											viewBox="0 0 14 14"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M5.25 10.5L8.75 7L5.25 3.5"
												stroke="#1f1f1f"
												strokeWidth="1.5"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
									</div>
								)}
							</FileRow>
						);
					})}
				</Sidebar>
				<PreviewPane>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							width: '100%',
							flex: 1,
							minHeight: 0,
						}}
					>
						<ArrowButton
							icon={<LeftOutlined />}
							onClick={handlePrevProof}
							disabled={proofUrls.indexOf(activeProofUrl) <= 0}
						/>
						<div
							style={{
								flex: 1,
								height: '100%',
								minHeight: '444px',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								overflow: 'hidden',
								background: '#d9d9d9',
								borderRadius: '2px',
							}}
						>
							{renderActiveFile()}
						</div>
						<ArrowButton
							icon={<RightOutlined />}
							onClick={handleNextProof}
							disabled={proofUrls.indexOf(activeProofUrl) === proofUrls.length - 1}
						/>
					</div>
					<DotContainer>
						{proofUrls.map((pUrl) => (
							<Dot
								key={pUrl}
								active={pUrl === activeProofUrl}
								onClick={() => setActiveProofUrl(pUrl)}
							/>
						))}
					</DotContainer>
				</PreviewPane>
			</PreviewLayout>
		);
	};

	const renderTabContent = () => {
		switch (activeTab) {
			case 'pod':
				return renderPodTab();
			case 'charges':
				return renderAdditionalChargesTab();
			case 'proof':
				return renderProofTab();
			default:
				return renderPodTab();
		}
	};

	return (
		<>
			<button
				style={{ border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}
				type="button"
				onClick={() => setOpen(true)}
			>
				<EyeOutlined style={{ fontSize: '16px' }} />
			</button>
			<Modal
				open={open}
				style={{ top: 20 }}
				width={1298}
				onCancel={handleCancel}
				footer={null}
				closable={false}
				bodyStyle={{ padding: 0 }}
			>
				<ModalWrapper>
					<ModalHeader>
						<CloseButton onClick={handleCancel}>
							<CloseOutlined />
						</CloseButton>
					</ModalHeader>

					<TabsContainer>
						<Tab active={activeTab === 'pod'} onClick={() => handleTabChange('pod')}>
							POD
						</Tab>
						<Tab
							active={activeTab === 'charges'}
							onClick={() => handleTabChange('charges')}
						>
							Additional Charges
						</Tab>
						<Tab
							active={activeTab === 'proof'}
							onClick={() => handleTabChange('proof')}
						>
							Proof of Additional Charges
						</Tab>
					</TabsContainer>
					{porBlocked && (
						<div
							style={{
								margin: '16px 24px 0',
								padding: '10px 14px',
								background: '#FFFBE6',
								border: '1px solid #FFE58F',
								borderRadius: '4px',
								color: '#8a6d3b',
								fontSize: '13px',
							}}
						>
							Please approve or reject PoR before taking action on PODs.
						</div>
					)}

					{renderTabContent()}

					{data?.status && data.status.toLowerCase() === 'pending' && (
						<FooterButtons>
							<ActionButton
								borderColor="#FF3B30"
								onClick={handleReject}
								disabled={porBlocked}
							>
								Reject
							</ActionButton>
							<ActionButton
								borderColor="#389E0E"
								onClick={handleApprove}
								disabled={porBlocked}
							>
								Approve
							</ActionButton>
						</FooterButtons>
					)}
				</ModalWrapper>
			</Modal>

			{/* Rejection Reason Modal */}
			<Modal
				open={showRejectModal}
				width={628}
				onCancel={handleRejectCancel}
				footer={null}
				closable={false}
				centered
				bodyStyle={{ padding: 0 }}
			>
				<RejectModalContent>
					<RejectFormGroup>
						<RejectModalTitle>Reason for Rejection</RejectModalTitle>
						<RejectFormGroup>
							<RejectLabel>Reason</RejectLabel>
							<RejectTextArea
								placeholder="Enter your comments here"
								value={rejectionReason}
								onChange={(e) => setRejectionReason(e.target.value)}
							/>
						</RejectFormGroup>
					</RejectFormGroup>
					<RejectModalButtons>
						<RejectCancelButton onClick={handleRejectCancel}>Cancel</RejectCancelButton>
						<RejectSubmitButton onClick={handleRejectSubmit} disabled={rejectLoading}>
							{rejectLoading ? 'Submitting...' : 'Submit'}
						</RejectSubmitButton>
					</RejectModalButtons>
				</RejectModalContent>
			</Modal>
		</>
	);
}

PodViewerModal.propTypes = {
	id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	data: PropTypes.shape({
		project_location: PropTypes.string,
		city: PropTypes.string,
		vendor_name: PropTypes.string,
		additional_charges: PropTypes.object,
		status: PropTypes.string,
		trip_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		driver_name: PropTypes.string,
		driver_pod_keys: PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string),
		]),
		por_approval_status: PropTypes.string,
	}),
};
